import React, { useEffect, useMemo, useState } from 'react';
import './PromptPackages.css';
import { PromptPackage, PromptPackageUpdate } from '../types';

interface PromptPackagesProps {
  prompts: PromptPackage[];
  selectedPrompt: PromptPackage | null;
  onSelectPrompt: (key: string) => void;
  onSavePrompt: (key: string, updates: PromptPackageUpdate) => Promise<void> | void;
  onCreatePrompt: (key: string, updates: PromptPackageUpdate) => Promise<void> | void;
  onPushToProduction: (key: string) => Promise<void> | void;
  onRefreshPrompt: () => void;
  onStartCreatePrompt: () => void;
  onCancelCreatePrompt: () => void;
  isLoading: boolean;
  isRefreshing: boolean;
  isCreating: boolean;
}

interface FormState {
  key: string;
  name: string;
  systemText: string;
  latexPrompt: string;
  routingText: string;
  realtimePrompt: string;
  description: string;
  stage: string;
  version: string;
  lastUpdated: string | null;
  isActive: boolean;
}

const defaultFormState: FormState = {
  key: '',
  name: '',
  systemText: '',
  latexPrompt: '',
  routingText: '',
  realtimePrompt: '',
  description: '',
  stage: '',
  version: '1.0',
  lastUpdated: null,
  isActive: true,
};

const PromptPackages: React.FC<PromptPackagesProps> = ({
  prompts,
  selectedPrompt,
  onSelectPrompt,
  onSavePrompt,
  onCreatePrompt,
  onPushToProduction,
  onRefreshPrompt,
  onStartCreatePrompt,
  onCancelCreatePrompt,
  isLoading,
  isRefreshing,
  isCreating,
}) => {
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPushing, setIsPushing] = useState(false);

  useEffect(() => {
    if (!selectedPrompt) {
      if (isCreating) {
        const now = new Date().toISOString();
        setFormState({
          ...defaultFormState,
          lastUpdated: now,
        });
      } else {
        setFormState(defaultFormState);
      }
      setHasChanges(false);
      return;
    }

    const nextState: FormState = {
      key: selectedPrompt.key || '',
      name: selectedPrompt.name || '',
      systemText: selectedPrompt.promptPackage?.systemText || '',
      latexPrompt: selectedPrompt.promptPackage?.latexPrompt || '',
      routingText: selectedPrompt.promptPackage?.routingText || '',
      realtimePrompt: selectedPrompt.promptPackage?.realtimePrompt || '',
      description: selectedPrompt.metadata?.description || '',
      stage: selectedPrompt.metadata?.stage || '',
      version: selectedPrompt.metadata?.version || '1.0',
      lastUpdated: selectedPrompt.metadata?.lastUpdated || null,
      isActive: typeof selectedPrompt.isActive === 'boolean' ? selectedPrompt.isActive : true,
    };

    setFormState(nextState);
    setHasChanges(false);
  }, [selectedPrompt, isCreating]);

  useEffect(() => {
    if (isCreating) {
      const now = new Date().toISOString();
      setFormState({
        ...defaultFormState,
        lastUpdated: now,
      });
      setHasChanges(false);
    }
  }, [isCreating]);

  const formattedLastUpdated = useMemo(() => {
    if (!formState.lastUpdated) return '—';
    const date = new Date(formState.lastUpdated);
    if (Number.isNaN(date.getTime())) return formState.lastUpdated;
    return date.toLocaleString();
  }, [formState.lastUpdated]);

  const handleFieldChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleDiscardChanges = () => {
    if (isCreating) {
      setHasChanges(false);
      onCancelCreatePrompt();
      return;
    }

    if (!selectedPrompt) {
      setFormState(defaultFormState);
      setHasChanges(false);
      return;
    }

    setFormState({
      key: selectedPrompt.key || '',
      name: selectedPrompt.name || '',
      systemText: selectedPrompt.promptPackage?.systemText || '',
      latexPrompt: selectedPrompt.promptPackage?.latexPrompt || '',
      routingText: selectedPrompt.promptPackage?.routingText || '',
      realtimePrompt: selectedPrompt.promptPackage?.realtimePrompt || '',
      description: selectedPrompt.metadata?.description || '',
      stage: selectedPrompt.metadata?.stage || '',
      version: selectedPrompt.metadata?.version || '1.0',
      lastUpdated: selectedPrompt.metadata?.lastUpdated || null,
      isActive: typeof selectedPrompt.isActive === 'boolean' ? selectedPrompt.isActive : true,
    });
    setHasChanges(false);
  };

  const handleSaveChanges = async () => {
    if (!selectedPrompt && !isCreating) return;
    const trimmedSystemText = formState.systemText.trim();
    if (!trimmedSystemText) return;
    if (isCreating && !formState.key.trim()) return;

    setIsSaving(true);
    try {
      const updates: PromptPackageUpdate = {
        name: formState.name,
        promptPackage: {
          systemText: formState.systemText,
          latexPrompt: formState.latexPrompt,
          routingText: formState.routingText,
          realtimePrompt: formState.realtimePrompt,
        },
        metadata: {
          description: formState.description,
          stage: formState.stage,
          version: formState.version || '1.0',
        },
        isActive: formState.isActive,
      };

      if (isCreating) {
        await onCreatePrompt(formState.key.trim(), updates);
      } else if (selectedPrompt) {
        await onSavePrompt(selectedPrompt.key, updates);
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save prompt package', error);
      return;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePushToProduction = async () => {
    if (!selectedPrompt) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to push "${selectedPrompt.name || selectedPrompt.key}" to production?\n\nThis will update the production MongoDB database.`
    );
    
    if (!confirmed) return;

    setIsPushing(true);
    try {
      await onPushToProduction(selectedPrompt.key);
    } catch (error) {
      console.error('Failed to push to production', error);
      alert(`Failed to push to production: ${error}`);
    } finally {
      setIsPushing(false);
    }
  };

  const canSave = !isSaving && formState.systemText.trim().length > 0 && (isCreating ? formState.key.trim().length > 0 : hasChanges);
  const canPush = !isCreating && !isSaving && !isPushing && selectedPrompt !== null;

  const isKeyEditable = isCreating;

  return (
    <div className="prompt-packages">
      <aside className="prompt-packages__list">
        <div className="prompt-packages__list-header">
          <h3>Prompt Packages</h3>
          <button
            type="button"
            className="secondary-btn"
            onClick={onRefreshPrompt}
            disabled={isRefreshing || isLoading}
            title="Reload prompt packages from server"
          >
            {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {isLoading ? (
          <div className="prompt-packages__empty">Loading prompt packages…</div>
        ) : prompts.length === 0 ? (
          <div className="prompt-packages__empty">No prompt packages found.</div>
        ) : (
          <ul>
            {prompts.map((prompt) => {
              const isSelected = !isCreating && selectedPrompt?.key === prompt.key;
              const stageLabel = prompt.metadata?.stage || '—';
              const updatedLabel = prompt.metadata?.lastUpdated
                ? new Date(prompt.metadata.lastUpdated).toLocaleDateString()
                : '—';
              return (
                <li key={prompt.key}>
                  <button
                    type="button"
                    className={isSelected ? 'selected' : ''}
                    onClick={() => onSelectPrompt(prompt.key)}
                  >
                    <span className="prompt-packages__list-name">{prompt.name || prompt.key}</span>
                    <span className="prompt-packages__list-meta">Stage: {stageLabel}</span>
                    <span className="prompt-packages__list-meta">Updated: {updatedLabel}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="prompt-packages__new">
          <button
            type="button"
            className="secondary-btn"
            onClick={onStartCreatePrompt}
            disabled={isCreating || isSaving}
          >
            ＋ New Prompt
          </button>
        </div>
      </aside>

      <section className="prompt-packages__detail">
        {!selectedPrompt && !isCreating ? (
          <div className="prompt-packages__empty-detail">
            {prompts.length === 0
              ? 'Add a prompt package to get started.'
              : 'Select a prompt package from the list or create a new one.'}
          </div>
        ) : (
          <div className="prompt-packages__form">
            <header className="prompt-packages__form-header">
              <div>
                <h2>{formState.name || selectedPrompt?.name || selectedPrompt?.key || 'New Prompt'}</h2>
                <p className="prompt-packages__form-key">Key: {formState.key || '—'}</p>
              </div>
              <div className="prompt-packages__form-meta">
                <div>
                  <label>Version</label>
                  <input type="text" value={formState.version} readOnly />
                </div>
                <div>
                  <label>Last Updated</label>
                  <input type="text" value={formattedLastUpdated} readOnly />
                </div>
              </div>
            </header>

            <div className="prompt-packages__form-row">
              <div className="field-group">
                <label htmlFor="prompt-key">Key</label>
                <input
                  id="prompt-key"
                  type="text"
                  value={formState.key}
                  onChange={(e) => handleFieldChange('key', e.target.value)}
                  placeholder="Enter a unique prompt key"
                  readOnly={!isKeyEditable}
                />
              </div>
              <div className="field-group">
                <label htmlFor="prompt-name">Name</label>
                <input
                  id="prompt-name"
                  type="text"
                  value={formState.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Friendly name for this prompt"
                />
              </div>
              <div className="field-group">
                <label htmlFor="prompt-stage">Stage</label>
                <input
                  id="prompt-stage"
                  type="text"
                  value={formState.stage}
                  onChange={(e) => handleFieldChange('stage', e.target.value)}
                  placeholder="production / staging / dev"
                />
              </div>
              <div className="field-group field-group--checkbox">
                <label htmlFor="prompt-active">Active</label>
                <input
                  id="prompt-active"
                  type="checkbox"
                  checked={formState.isActive}
                  onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                />
              </div>
            </div>

            <div className="field-group">
              <label htmlFor="prompt-description">Description</label>
              <textarea
                id="prompt-description"
                value={formState.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                rows={3}
              />
            </div>

            <div className="field-group">
              <label htmlFor="prompt-realtime-text">Realtime Prompt</label>
              <textarea
                id="prompt-realtime-text"
                value={formState.realtimePrompt}
                onChange={(e) => handleFieldChange('realtimePrompt', e.target.value)}
                rows={8}
                placeholder="Enter the realtime prompt instructions…"
              />
            </div>

            <div className="field-group">
              <label htmlFor="prompt-system-text">System Text</label>
              <textarea
                id="prompt-system-text"
                value={formState.systemText}
                onChange={(e) => handleFieldChange('systemText', e.target.value)}
                rows={12}
                placeholder="Enter the tutor system prompt…"
              />
            </div>

            <div className="field-group">
              <label htmlFor="prompt-latex-text">LaTeX Prompt</label>
              <textarea
                id="prompt-latex-text"
                value={formState.latexPrompt}
                onChange={(e) => handleFieldChange('latexPrompt', e.target.value)}
                rows={8}
                placeholder="Enter the LaTeX formatter prompt…"
              />
            </div>

            <div className="field-group">
              <label htmlFor="prompt-routing-text">Routing Prompt</label>
              <textarea
                id="prompt-routing-text"
                value={formState.routingText}
                onChange={(e) => handleFieldChange('routingText', e.target.value)}
                rows={8}
                placeholder="Enter the routing instructions…"
              />
            </div>

            <div className="prompt-packages__actions">
              <button
                type="button"
                className="secondary-btn"
                onClick={handleDiscardChanges}
                disabled={isSaving || (!hasChanges && !isCreating)}
              >
                {isCreating ? 'Cancel' : 'Discard Changes'}
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleSaveChanges}
                disabled={!canSave}
              >
                {isSaving ? 'Saving…' : isCreating ? 'Create Prompt' : 'Save Changes'}
              </button>
              {!isCreating && selectedPrompt && (
                <button
                  type="button"
                  className="primary-btn push-to-prod-btn"
                  onClick={handlePushToProduction}
                  disabled={!canPush}
                  title="Push this prompt package to production database"
                >
                  {isPushing ? 'Pushing…' : '🚀 Push to Prod'}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default PromptPackages;
